import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Mail, Building2, MessageCircle, Send, Heart, Flame, ThumbsUp, Sparkles } from 'lucide-react';
import { useAuthStore } from '@/features/auth';
import { useCreateConversation } from '@/features/messaging/hooks';
import { setPendingChat, getMessagingFlow } from '@/features/messaging/messagingNavBridge';
import { MODAL_COLORS } from '@/components/modal/constants';
import { FormModal } from '@/components/modal/FormModal';
import { UserAvatar } from '@/components/ui';
import { useUserProfile } from '../hooks';
import type { ReactionType } from '../types';

const ROLE_COLORS: Record<string, { bg: string; text: string }> = {
  SUPER_ADMIN: { bg: '#1e293b', text: '#ffffff' },
  ADMIN: { bg: 'var(--color-primary-500)', text: '#ffffff' },
  MANAGER: { bg: '#e8eaf6', text: '#283593' },
  THERAPIST: { bg: '#e8f5e9', text: '#2e7d32' },
};

const REACTION_ICONS: Record<ReactionType, typeof ThumbsUp> = {
  like: ThumbsUp,
  fire: Flame,
  heart: Heart,
  smooth: Sparkles,
};

interface UserProfileModalProps {
  userId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

function ProfileSkeleton() {
  const shimmer = 'animate-pulse rounded';
  return (
    <div className="space-y-5">
      {/* Avatar + name */}
      <div className="flex flex-col items-center gap-3">
        <div className={`h-20 w-20 rounded-full ${shimmer}`} style={{ backgroundColor: MODAL_COLORS.border }} />
        <div className={`h-5 w-36 ${shimmer}`} style={{ backgroundColor: MODAL_COLORS.border }} />
        <div className={`h-4 w-24 ${shimmer}`} style={{ backgroundColor: MODAL_COLORS.border }} />
      </div>
      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className={`h-16 ${shimmer}`} style={{ backgroundColor: MODAL_COLORS.border }} />
        ))}
      </div>
      {/* Content lines */}
      <div className="space-y-2">
        <div className={`h-4 w-full ${shimmer}`} style={{ backgroundColor: MODAL_COLORS.border }} />
        <div className={`h-4 w-3/4 ${shimmer}`} style={{ backgroundColor: MODAL_COLORS.border }} />
      </div>
    </div>
  );
}

export function UserProfileModal({ userId, isOpen, onClose }: UserProfileModalProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);
  const { data: profile, isLoading, isError } = useUserProfile(isOpen ? userId : null);
  const createConversation = useCreateConversation();

  const isSelf = currentUser?.id === userId;
  const isEn = i18n.language === 'en';

  const handleSendMessage = useCallback(async () => {
    if (!userId) return;
    try {
      const result = await createConversation.mutateAsync({
        type: 'dm',
        participantIds: [userId],
      });
      const conversationId = result.conversation.id;
      onClose();

      // If messaging is already open, use the bridge directly
      const flow = getMessagingFlow();
      if (flow) {
        flow.push('ChatActivity', { conversationId });
        navigate('/messages');
      } else {
        // Messaging not mounted yet — set pending and navigate
        setPendingChat(conversationId);
        navigate('/messages');
      }
    } catch {
      onClose();
      navigate('/messages');
    }
  }, [userId, createConversation, onClose, navigate]);

  const formatDate = (iso: string) => {
    return new Date(iso).toLocaleDateString(i18n.language === 'he' ? 'he-IL' : 'en-US', {
      year: 'numeric',
      month: 'short',
    });
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('userProfile.title')}
      headerStyle={{
        backgroundColor: 'rgba(255,255,255,0.55)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        color: '#1e293b',
        borderBottom: '2px solid rgba(255,255,255,0.25)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      }}
    >
      {isLoading ? (
        <ProfileSkeleton />
      ) : isError || !profile ? (
        <div className="py-12 text-center">
          <p className="text-sm font-medium" style={{ color: MODAL_COLORS.text }}>
            {t('userProfile.profileNotFound')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* ── Identity Hero ── */}
          <div className="flex flex-col items-center gap-2 pt-2">
            <UserAvatar
              src={profile.avatarUrl}
              name={`${profile.firstName ?? ''} ${profile.lastName ?? ''}`}
              size="xl"
              className="border-2 border-[var(--user-page-card-border)]"
            />
            <h2 className="text-lg font-bold" style={{ color: MODAL_COLORS.text }}>
              {[profile.firstName, profile.lastName].filter(Boolean).join(' ')}
            </h2>
            <div className="flex items-center gap-2">
              {(() => {
                const roleStyle = ROLE_COLORS[profile.role] ?? ROLE_COLORS.THERAPIST;
                return (
                  <span
                    className="text-[11px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide"
                    style={{ backgroundColor: roleStyle.bg, color: roleStyle.text }}
                  >
                    {profile.role.replace('_', ' ')}
                  </span>
                );
              })()}
            </div>
            {profile.employmentRole && (
              <p className="text-sm" style={{ color: '#65676B' }}>
                {isEn ? (profile.employmentRole.nameEn || profile.employmentRole.name) : profile.employmentRole.name}
              </p>
            )}
          </div>

          {/* ── Stats Row ── */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: t('userProfile.reportsThisMonth'), value: profile.reportCountThisMonth },
              { label: t('userProfile.totalPosts'), value: profile.postCount },
              { label: t('userProfile.memberSince'), value: formatDate(profile.createdAt) },
            ].map((stat) => (
              <div
                key={stat.label}
                className="dock-icon-glass dock-glass-subtle rounded-xl p-3 text-center"
                style={{ borderRadius: 16 }}
              >
                <div className="text-lg font-bold" style={{ color: MODAL_COLORS.text }}>
                  {stat.value}
                </div>
                <div className="text-[10px] font-medium mt-0.5" style={{ color: '#65676B' }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

          {/* ── Email (admin only) ── */}
          {profile.email && (
            <div
              className="dock-icon-glass dock-glass-subtle flex items-center gap-3 rounded-xl p-3"
              style={{ borderRadius: 16 }}
            >
              <Mail className="h-4 w-4 flex-shrink-0" style={{ color: '#65676B' }} />
              <div>
                <div className="text-[10px] font-medium uppercase tracking-wide" style={{ color: '#65676B' }}>
                  {t('userProfile.email')}
                </div>
                <div className="text-sm font-medium" style={{ color: MODAL_COLORS.text }}>
                  {profile.email}
                </div>
              </div>
            </div>
          )}

          {/* ── Hospitals ── */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#65676B' }}>
              {t('userProfile.hospitals')}
            </h3>
            {profile.hospitals.length === 0 ? (
              <p className="text-sm" style={{ color: '#65676B' }}>
                {t('userProfile.noHospitals')}
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {profile.hospitals.map((h) => (
                  <span
                    key={h.id}
                    className="dock-icon-glass dock-glass-subtle inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full"
                    style={{ color: MODAL_COLORS.text }}
                  >
                    <Building2 className="h-3 w-3" style={{ color: '#65676B' }} />
                    {isEn ? (h.nameEn || h.name) : h.name}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* ── Recent Posts ── */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#65676B' }}>
              {t('userProfile.recentPosts')}
            </h3>
            {profile.recentPosts.length === 0 ? (
              <p className="text-sm" style={{ color: '#65676B' }}>
                {t('userProfile.noRecentPosts')}
              </p>
            ) : (
              <div className="space-y-2">
                {profile.recentPosts.map((post) => (
                  <div
                    key={post.id}
                    className="dock-icon-glass dock-glass-subtle rounded-xl p-3"
                    style={{ borderRadius: 16 }}
                  >
                    <p className="text-sm line-clamp-2" style={{ color: MODAL_COLORS.text }}>
                      {post.content}
                    </p>
                    <div className="flex items-center gap-3 mt-2">
                      {/* Reaction counts */}
                      {Object.entries(post.reactionCounts).map(([type, count]) => {
                        const Icon = REACTION_ICONS[type as ReactionType];
                        if (!Icon || !count) return null;
                        return (
                          <span key={type} className="flex items-center gap-0.5 text-[11px]" style={{ color: '#65676B' }}>
                            <Icon className="h-3 w-3" />
                            {count}
                          </span>
                        );
                      })}
                      {/* Comment count */}
                      {post.commentCount > 0 && (
                        <span className="flex items-center gap-0.5 text-[11px]" style={{ color: '#65676B' }}>
                          <MessageCircle className="h-3 w-3" />
                          {post.commentCount}
                        </span>
                      )}
                      <span className="text-[11px] ms-auto" style={{ color: '#9CA3AF' }}>
                        {formatDate(post.createdAt)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Send Message ── */}
          {!isSelf && (
            <button
              onClick={handleSendMessage}
              disabled={createConversation.isPending}
              className="dock-icon-glass dock-glass-subtle w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold transition-all disabled:opacity-50"
              style={{ borderRadius: 16, color: '#1a4fa0' }}
            >
              <Send className="h-4 w-4" />
              {t('userProfile.sendMessage')}
            </button>
          )}
        </div>
      )}
    </FormModal>
  );
}
